import React from 'react';
import { amountToWordsSoles } from '../../utils/amountToWords';
import { getItemPrintLineDisplay } from '../../utils/unitSelectUtils';
import { formatLinkedRequirementCodes } from '../../utils/purchaseOrderRequirements';
import {
  formatPrintDate,
  resolveOrderPaymentPrintDate,
} from '../../utils/dateUtils';

function buildPrintData(order) {
  const orderDate = formatPrintDate(order.order_date);

  const deliveryDate = order.expected_delivery_date
    ? formatPrintDate(order.expected_delivery_date)
    : '';

  let paymentCondition = '';
  if (order.payment_condition_type === 'cash') {
    paymentCondition = 'Al contado';
  } else if (order.payment_condition_type === 'days') {
    paymentCondition = `Cada ${order.payment_condition_days} días (máx. 3 meses)`;
  } else if (order.payment_condition_type === 'date') {
    const paymentDateStr = formatPrintDate(order.payment_condition_date);
    paymentCondition = `Fecha específica: ${paymentDateStr}`;
  }

  const paymentDateNumeric = resolveOrderPaymentPrintDate(order);

  const formatMoney = (n) =>
    parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatUnitPrice = (p) =>
    parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  const statusText = order.status === 'issued' ? 'Emitida' : 'Cancelada';
  const statusColor = order.status === 'issued' ? '#10b981' : '#ef4444';
  const docTypeLabel =
    order.supplier?.document_type === 'ruc'
      ? 'RUC'
      : order.supplier?.document_type === 'dni'
        ? 'DNI'
        : order.supplier?.document_type
          ? String(order.supplier.document_type).toUpperCase()
          : 'Documento';

  return {
    orderDate,
    deliveryDate,
    paymentCondition,
    paymentDateNumeric,
    formatMoney,
    formatUnitPrice,
    statusText,
    statusColor,
    docTypeLabel,
    igvRatePercent: parseFloat(order.igv_rate ?? 18),
    baseImponible: formatMoney(order.base_imponible),
    igvAmount: formatMoney(order.igv_amount),
    noGravados: formatMoney(order.no_gravados),
    totalAmount: formatMoney(order.total_amount),
    totalInWords: amountToWordsSoles(order.total_amount),
  };
}

const PurchaseOrderPrintDocument = ({ order, productConversionsEnabled = true }) => {
  if (!order) return null;

  const data = buildPrintData(order);
  const requirementCodes = formatLinkedRequirementCodes(order);

  return (
    <div className="print-container">
      <div className="header">
        <h1>Orden de Compra</h1>
      </div>

      <div className="info-compact">
        <div className="info-compact-line">
          <span className="info-compact-label">Orden:</span> {order.code}
          <span className="info-compact-sep"> · </span>
          <span className="info-compact-label">Fecha:</span> {data.orderDate}
          {data.deliveryDate && (
            <>
              <span className="info-compact-sep"> · </span>
              <span className="info-compact-label">Entrega:</span> {data.deliveryDate}
            </>
          )}
          <span className="info-compact-sep"> · </span>
          <span className="info-compact-label">Estado:</span>{' '}
          <span
            className="status-badge"
            style={{ backgroundColor: `${data.statusColor}20`, color: data.statusColor }}
          >
            {data.statusText}
          </span>
        </div>
        <div className="info-compact-line">
          <span className="info-compact-label">Proveedor:</span> {order.supplier?.name || 'N/A'}
          {order.supplier?.document_number && (
            <>
              <span className="info-compact-sep"> · </span>
              <span className="info-compact-label">{data.docTypeLabel}:</span> {order.supplier.document_number}
            </>
          )}
          {order.supplier?.address && (
            <>
              <span className="info-compact-sep"> · </span>
              <span className="info-compact-label">Dir.:</span> {order.supplier.address}
            </>
          )}
        </div>
        <div className="info-compact-line">
          <span className="info-compact-label">Condición de pago:</span> {data.paymentCondition}
          {data.paymentDateNumeric && order.payment_condition_type !== 'cash' && (
            <>
              <span className="info-compact-sep"> · </span>
              <span className="info-compact-label">Fecha de pago:</span> {data.paymentDateNumeric}
            </>
          )}
        </div>
        {requirementCodes ? (
          <div className="info-compact-line">
            <span className="info-compact-label">
              {requirementCodes.includes(',') ? 'Requerimientos:' : 'Requerimiento:'}
            </span>{' '}
            {requirementCodes}
          </div>
        ) : null}
      </div>

      <div className="items-card">
        <div className="items-title">Items de la Orden</div>
        <table className="items-table">
          <thead>
            <tr>
              <th>Descripción</th>
              <th>Unidad</th>
              <th>Proyecto</th>
              <th className="num">Cant.</th>
              <th className="num">P.Unit</th>
              <th className="num">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((item, index) => {
              const line = getItemPrintLineDisplay({
                item,
                productConversionsEnabled,
                useConvertedQuantityForSubtotal: order.use_converted_quantity_for_subtotal !== false,
              });
              return (
                <tr key={item.id ?? index}>
                  <td>
                    <span className="product-name">{item.product?.name || 'N/A'}</span>
                  </td>
                  <td>{line.unit}</td>
                  <td>{item.project ? `${item.project.code} - ${item.project.name}` : '-'}</td>
                  <td className="num">{line.quantity}</td>
                  <td className="num">S/ {data.formatUnitPrice(item.unit_price)}</td>
                  <td className="num">
                    <strong>S/ {data.formatMoney(line.subtotal)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="print-footer-compact">
        <div className="print-footer-obs">
          <span className="print-obs-label">Observaciones:</span>
          {order.notes ? <span className="print-obs-text">{order.notes}</span> : null}
        </div>
        <table className="footer-totals-table">
          <colgroup>
            <col className="col-desc" />
            <col className="col-unit" />
            <col className="col-project" />
            <col className="col-qty" />
            <col className="col-price" />
            <col className="col-subtotal" />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan={5} className="footer-totals-label">Base:</td>
              <td className="footer-totals-amount">S/ {data.baseImponible}</td>
            </tr>
            <tr>
              <td colSpan={5} className="footer-totals-label">IGV ({data.igvRatePercent}%):</td>
              <td className="footer-totals-amount">S/ {data.igvAmount}</td>
            </tr>
            {parseFloat(order.no_gravados || 0) > 0 && (
              <tr>
                <td colSpan={5} className="footer-totals-label">No grav.:</td>
                <td className="footer-totals-amount">S/ {data.noGravados}</td>
              </tr>
            )}
            <tr>
              <td colSpan={5} className="footer-totals-label footer-totals-label-final">Total:</td>
              <td className="footer-totals-amount footer-totals-amount-final">S/ {data.totalAmount}</td>
            </tr>
            <tr>
              <td colSpan={5} className="footer-totals-label footer-totals-label-words">Son:</td>
              <td colSpan={1} className="footer-totals-amount footer-totals-amount-words">{data.totalInWords}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseOrderPrintDocument;
